import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { ArrowLeft, CircleDollarSign, FilePlus2, Pencil, RefreshCw, Search } from "lucide-react";
import { Link, Navigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { useAuth } from "@/auth/AuthContext";
import BoardLoading from "@/components/BoardLoading";
import BoardWorkspace from "@/components/BoardWorkspace";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { InvoiceEditorDialog } from "@/components/InvoiceEditorDialog";
import { TypedDateInput } from "@/components/TypedDateInput";
import { statusOptions } from "@/components/invoiceTrackingUtils";
import { createConflictHandler, attachExpectedVersion } from "@/lib/conflictHandler";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/operations`;
const statusLabel = (value) =>
  statusOptions.find(([key]) => key === value)?.[1] ||
  (value || "Not set").replaceAll("_", " ");
const currency = (value) =>
  new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
  }).format(Number(value || 0));

export default function InvoiceWorkspace() {
  const { canAccessBilling, user } = useAuth();
  const [data, setData] = useState(null);
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState("active");
  const [search, setSearch] = useState("");
  const [editor, setEditor] = useState(null);
  const [error, setError] = useState("");

  const loadInvoices = useCallback(async () => {
    try {
      setError("");
      const response = await axios.get(`${API}/invoices/queue`, {
        params: { filter, search },
      });
      setData(response.data);
    } catch (requestError) {
      setError(
        requestError.response?.data?.detail || "Invoices could not be loaded.",
      );
    }
  }, [filter, search]);

  const loadProjects = async () => {
    const response = await axios.get(`${API}/master-project-board`);
    setProjects(response.data.rows || []);
  };

  useEffect(() => {
    if (canAccessBilling) loadInvoices();
  }, [canAccessBilling, loadInvoices]);

  const openEditor = async (nextEditor) => {
    await loadProjects();
    setEditor(nextEditor);
  };

  const applyRetention = async (invoice, action) => {
    const labels = {
      archive: "archive this Draft invoice",
      restore: "restore this archived invoice",
      void: "void this invoice",
      credit: "credit this invoice",
      purge: "permanently purge this archived invoice",
    };
    if (!window.confirm(`Confirm: ${labels[action]}?`)) return;
    const handleConflict = createConflictHandler(loadInvoices);
    try {
      await axios.post(`${API}/invoices/${invoice.id}/retention/${action}`, { reason: "" });
      toast.success(`Invoice ${action} action saved.`);
      await loadInvoices();
    } catch (requestError) {
      if (!handleConflict(requestError)) {
        toast.error(requestError.response?.data?.detail || "Invoice action could not be saved.");
      }
    }
  };

  const finalizationVoid = async (invoice) => {
    const reason = window.prompt("Reason to void this finalized invoice? (required)");
    if (!reason || !reason.trim()) return;
    const handleConflict = createConflictHandler(loadInvoices);
    try {
      await axios.post(`${API}/invoices/${invoice.id}/void`, { reason: reason.trim() });
      toast.success("Invoice voided.");
      await loadInvoices();
    } catch (error) {
      if (!handleConflict(error)) {
        const detail = error?.response?.data?.detail;
        toast.error(typeof detail === "string" ? detail : detail?.message || "Void failed.");
      }
    }
  };

  const finalizationSupersede = async (invoice) => {
    const reason = window.prompt(
      "Reason to supersede this finalized invoice? (required)\n\n"
      + "A new invoice (waiting_to_submit) will be created that replaces this one.\n"
      + "The original financial record is preserved.",
    );
    if (!reason || !reason.trim()) return;
    const handleConflict = createConflictHandler(loadInvoices);
    try {
      const r = await axios.post(`${API}/invoices/${invoice.id}/supersede`, {
        reason: reason.trim(),
      });
      toast.success(`Superseded. New invoice: ${r.data.invoice_number}`);
      await loadInvoices();
    } catch (error) {
      if (!handleConflict(error)) {
        const detail = error?.response?.data?.detail;
        toast.error(typeof detail === "string" ? detail : detail?.message || "Supersede failed.");
      }
    }
  };

  const finalizationNotesCorrection = async (invoice) => {
    const notes = window.prompt(
      "New notes for this finalized invoice:",
      invoice.notes || "",
    );
    if (notes === null) return;
    const reason = window.prompt("Reason for this correction? (required)");
    if (!reason || !reason.trim()) return;
    const handleConflict = createConflictHandler(loadInvoices);
    try {
      await axios.post(`${API}/invoices/${invoice.id}/correction`, {
        kind: "notes_only",
        reason: reason.trim(),
        notes,
      });
      toast.success("Notes correction saved.");
      await loadInvoices();
    } catch (error) {
      if (!handleConflict(error)) {
        const detail = error?.response?.data?.detail;
        toast.error(typeof detail === "string" ? detail : detail?.message || "Correction failed.");
      }
    }
  };

  if (!canAccessBilling) return <Navigate replace to="/master-project-board" />;

  if (!data && !error) return <BoardLoading label="Loading Pending Invoices" />;

  return (
    <BoardWorkspace
      eyebrow="Office billing workflow"
      title="Pending Invoices"
      subtitle="Track invoices that were created or sent, their approval state, and the work they represent."
      actions={
        <div className="invoice-heading-actions">
          <button
            className="board-refresh-button"
            data-testid="invoice-refresh-button"
            onClick={loadInvoices}
            type="button"
          >
            <RefreshCw size={15} />
            Refresh
          </button>
          <button
            className="invoice-primary-button"
            data-testid="invoice-create-button"
            onClick={() => openEditor({ mode: "create" })}
            type="button"
          >
            <FilePlus2 size={15} />
            Track invoice
          </button>
        </div>
      }
    >
      {error ? (
        <section className="board-error-state" data-testid="invoice-error">
          <p>{error}</p>
          <button
            data-testid="invoice-retry-button"
            onClick={loadInvoices}
            type="button"
          >
            Try again
          </button>
        </section>
      ) : (
        <>
          <section
            className="invoice-summary-grid"
            data-testid="invoice-summary"
          >
            <SummaryCard
              label="Waiting approval"
              testId="invoice-summary-waiting-approval"
              value={data.dashboard.waiting_approval}
            />
            <SummaryCard
              label="Oldest wait"
              testId="invoice-summary-oldest-wait"
              value={`${data.dashboard.oldest_waiting} days`}
            />
            <SummaryCard
              label="Whole-company records"
              testId="invoice-summary-unlinked-company"
              value={data.dashboard.unlinked_whole_company || 0}
            />
          </section>
          <section
            className="board-filter-bar invoice-filter-bar"
            data-testid="invoice-filters"
          >
            <label className="board-search-control" htmlFor="invoice-search">
              <Search size={16} />
              <input
                data-testid="invoice-search-input"
                id="invoice-search"
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search invoice, project, customer"
                value={search}
              />
            </label>
            <label className="board-filter-control" htmlFor="invoice-filter">
              <span>View</span>
              <select
                data-testid="invoice-filter-select"
                id="invoice-filter"
                onChange={(event) => setFilter(event.target.value)}
                value={filter}
              >
                <option value="active">Open / Pending</option>
                <option value="all">All</option>
                <option value="paid">Paid</option>
                <option value="waiting_to_submit">Waiting to Submit</option>
                <option value="waiting_for_approval">
                  Waiting for Approval
                </option>
                <option value="approved">Approved</option>
                <option value="unlinked">Whole-company review</option>
                <option value="owner_review">Owner review needed</option>
                <option value="archived">Archived</option>
              </select>
            </label>
            <span
              className="board-results-count"
              data-testid="invoice-results-count"
            >
              {data.invoices.length} shown
            </span>
          </section>
          <section
            className="invoice-table-panel"
            data-testid="invoice-table-panel"
          >
            <div className="board-table-caption">
              <div>
                <span className="operations-kicker">
                  Sent and approval tracking
                </span>
                <h2>Invoice queue</h2>
              </div>
              <p>
                Invoice creation here records an existing invoice; it does not
                generate a customer invoice document.
              </p>
            </div>
            <div className="board-table-scroll">
              <table
                className="operations-data-table invoice-data-table"
                data-testid="invoice-table"
              >
                <thead>
                  <tr>
                    <th>
                      <span>Invoice</span>
                    </th>
                    <th>
                      <span>Project</span>
                    </th>
                    <th>
                      <span>Date sent</span>
                    </th>
                    <th>
                      <span>Approval state</span>
                    </th>
                    <th>
                      <span>Payment state</span>
                    </th>
                    <th>
                      <span>Value / work</span>
                    </th>
                    <th>
                      <span>Customer</span>
                    </th>
                    <th>
                      <span>Owner</span>
                    </th>
                    <th>
                      <span>Notes</span>
                    </th>
                    <th>
                      <span>Update</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.invoices.map((invoice) => (
                    <InvoiceRow
                      invoice={invoice}
                      key={invoice.id}
                      onEdit={() => openEditor({ mode: "edit", invoice })}
                      onPayment={() => setEditor({ mode: "payment", invoice })}
                      onRetention={applyRetention}
                      onFinalizationVoid={finalizationVoid}
                      onFinalizationSupersede={finalizationSupersede}
                      onFinalizationNotesCorrection={finalizationNotesCorrection}
                      canPurge={user?.role === "owner_admin"}
                      isOwnerAdmin={user?.role === "owner_admin"}
                    />
                  ))}
                </tbody>
              </table>
              {!data.invoices.length && (
                <div
                  className="board-empty-state"
                  data-testid="invoice-empty-state"
                >
                  No invoices match this view.
                </div>
              )}
            </div>
          </section>
        </>
      )}
      <InvoiceEditorDialog
        editor={editor}
        onOpenChange={() => setEditor(null)}
        onSaved={() => {
          setEditor(null);
          loadInvoices();
        }}
        projects={projects}
      />
      <InvoicePaymentDialog
        editor={editor?.mode === "payment" ? editor : null}
        onOpenChange={() => setEditor(null)}
        onSaved={() => {
          setEditor(null);
          loadInvoices();
        }}
      />
      <Toaster richColors position="bottom-right" />
    </BoardWorkspace>
  );
}

function SummaryCard({ label, testId, value }) {
  return (
    <article className="invoice-summary-card" data-testid={testId}>
      <span>{label}</span>
      <strong>{value}</strong>
    </article>
  );
}
function InvoiceRow({
  canPurge,
  invoice,
  onEdit,
  onPayment,
  onRetention,
  onFinalizationVoid,
  onFinalizationSupersede,
  onFinalizationNotesCorrection,
  isOwnerAdmin,
}) {
  const isFinalized = Boolean(invoice.finalized_at);
  const canVoid = isFinalized && Number(invoice.paid_amount || 0) === 0 && isOwnerAdmin;
  const canSupersede = isFinalized && isOwnerAdmin && !invoice.superseded_by;
  const canNotesCorrection = isFinalized && !invoice.superseded_by;
  return (
    <tr data-testid={`invoice-row-${invoice.id}`}>
      <td>
        <strong>{invoice.invoice_number}</strong>
      </td>
      <td>
        {invoice.unlinked_whole_company
          ? `Whole-company · ${invoice.project_number || "Unlinked"}`
          : invoice.project_number || "—"}
      </td>
      <td>{invoice.date_submitted || invoice.invoice_date || "—"}</td>
      <td>
        <div className="invoice-approval-state">
          <span className={`invoice-status status-${invoice.status}`}>
            {statusLabel(invoice.status)}
          </span>
          {invoice.finalized_at && (
            <span
              className="invoice-status status-finalized"
              data-testid={`invoice-finalized-${invoice.id}`}
              title={`Finalized on ${invoice.finalized_at.slice(0, 10)} — corrections go through void or supersede.`}
              style={{ background: "#fef3c7", color: "#78350f", fontWeight: 600 }}
            >
              Finalized
            </span>
          )}
        </div>
      </td>
      <td>
        <span className={`invoice-status status-${invoice.payment_status || "unpaid"}`}>
          {(invoice.payment_status || "unpaid").replaceAll("_", " ")}
        </span>
      </td>
      <td>
        <div className="invoice-work-cell">
          <strong>{currency(invoice.invoice_amount)}</strong>
          <span>
            {invoice.work_description || invoice.notes || "No work description"}
          </span>
        </div>
      </td>
      <td>
        {invoice.customer || (invoice.unlinked_whole_company ? "Not linked" : "—")}
      </td>
      <td>{invoice.owner || "—"}</td>
      <td
        className="table-work-cell"
        title={invoice.notes || ""}
      >
        {invoice.notes ||
          "—"}
      </td>
      <td>
        <div className="invoice-row-actions">
          <button
            className="table-edit-button"
            data-testid={`invoice-edit-${invoice.id}`}
            onClick={onEdit}
            type="button"
          >
            <Pencil size={13} />
            Update
          </button>
          {invoice.status === "approved" && !isFinalized && (
            <button
              className="table-edit-button"
              data-testid={`invoice-payment-${invoice.id}`}
              onClick={onPayment}
              type="button"
            >
              <CircleDollarSign size={13} />
              Record payment
            </button>
          )}
          {canVoid && (
            <button
              className="table-edit-button"
              data-testid={`invoice-finalization-void-${invoice.id}`}
              onClick={() => onFinalizationVoid(invoice)}
              type="button"
              style={{ background: "#fee2e2", color: "#991b1b" }}
              title="Void this finalized (unpaid) invoice"
            >
              Void
            </button>
          )}
          {canSupersede && (
            <button
              className="table-edit-button"
              data-testid={`invoice-supersede-${invoice.id}`}
              onClick={() => onFinalizationSupersede(invoice)}
              type="button"
              style={{ background: "#dbeafe", color: "#1e40af" }}
              title="Create a corrected replacement — original stays intact"
            >
              Supersede
            </button>
          )}
          {canNotesCorrection && (
            <button
              className="table-edit-button"
              data-testid={`invoice-notes-correction-${invoice.id}`}
              onClick={() => onFinalizationNotesCorrection(invoice)}
              type="button"
              title="Edit notes only — financials untouched"
            >
              Notes correction
            </button>
          )}
          {!invoice.archived && invoice.status === "waiting_to_submit" && (
            <button
              data-testid={`invoice-archive-${invoice.id}`}
              onClick={() => onRetention(invoice, "archive")}
              type="button"
            >
              Archive Draft
            </button>
          )}
          {!invoice.archived && invoice.status !== "waiting_to_submit" && !isFinalized && (
            <button
              data-testid={`invoice-void-${invoice.id}`}
              onClick={() => onRetention(invoice, "void")}
              type="button"
            >
              Void
            </button>
          )}
          {!invoice.archived && invoice.status !== "waiting_to_submit" && (
            <button
              data-testid={`invoice-credit-${invoice.id}`}
              onClick={() => onRetention(invoice, "credit")}
              type="button"
            >
              Credit
            </button>
          )}
          {invoice.archived && (
            <button
              data-testid={`invoice-restore-${invoice.id}`}
              onClick={() => onRetention(invoice, "restore")}
              type="button"
            >
              Restore
            </button>
          )}
          {invoice.archived && canPurge && (
            <button
              data-testid={`invoice-purge-${invoice.id}`}
              onClick={() => onRetention(invoice, "purge")}
              type="button"
            >
              Purge
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

function InvoicePaymentDialog({ editor, onOpenChange, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editor) {
      const invoice = editor.invoice;
      const invoiceAmount = Number(invoice.invoice_amount || 0);
      const previouslyPaid = Number(invoice.paid_amount || 0);
      const isFullyPaid = previouslyPaid >= invoiceAmount && invoiceAmount > 0;
      setForm({
        payment_amount: String(isFullyPaid ? 0 : Math.max(invoiceAmount - previouslyPaid, 0)),
        payment_date: isFullyPaid ? "" : new Date().toISOString().slice(0, 10),
        payment_reference: isFullyPaid ? "" : invoice.payment_reference || "",
        notes: "",
        is_reversal: isFullyPaid,
      });
    } else {
      setForm(null);
    }
  }, [editor]);
  const save = async (event) => {
    event.preventDefault();
    if (!form || !editor || !editor.invoice?.id) return;
    const invoiceId = editor.invoice.id;
    const previousPaid = Number(editor.invoice.paid_amount || 0);
    const invoiceAmount = Number(editor.invoice.invoice_amount || 0);
    const paymentAmount = Number(form.payment_amount || 0);
    const nextPaidAmount = form.is_reversal
      ? 0
      : Math.min(invoiceAmount, previousPaid + paymentAmount);
    setSaving(true);
    const handleConflict = createConflictHandler(onSaved);
    try {
      await axios.patch(
        `${API}/invoices/${invoiceId}/payment`,
        attachExpectedVersion(
          {
            paid_amount: nextPaidAmount,
            payment_date: form.is_reversal ? "" : form.payment_date,
            payment_reference: form.is_reversal ? "" : form.payment_reference,
            notes: form.notes,
          },
          editor.invoice,
        ),
      );
      toast.success(
        form.is_reversal
          ? "Payment reversed. Production totals were recalculated."
          : "Payment recorded. Production totals were recalculated.",
      );
      onSaved();
    } catch (error) {
      if (!handleConflict(error)) {
        toast.error(error.response?.data?.detail || "Payment could not be recorded.");
      }
    } finally {
      setSaving(false);
    }
  };

  if (!form) return null;

  return (
    <Dialog onOpenChange={onOpenChange} open={Boolean(editor)}>
      <DialogContent
        className="invoice-editor-dialog"
        data-testid="invoice-payment-dialog"
      >
        <DialogHeader>
          <p className="operations-kicker">Office payment assessment</p>
          <DialogTitle>{editor?.invoice?.invoice_number} payment</DialogTitle>
          <DialogDescription>
            {form.is_reversal
              ? "Clear this paid record with a reversal reason. Its payment fields and Production "
                  + "Tracker amount will be removed."
              : "Record the amount received now. The paid date defaults to today and Production "
                  + "Tracker uses that paid week."}
          </DialogDescription>
        </DialogHeader>
        <form
          className="invoice-editor-form"
          data-testid="invoice-payment-form"
          onSubmit={save}
        >
          <label htmlFor="payment-amount">
            {form.is_reversal ? "Amount to reverse" : "Amount received now"}
            <Input
              data-testid="invoice-payment-amount-input"
              id="payment-amount"
              min="0"
              onChange={(event) =>
                setForm({ ...form, payment_amount: event.target.value })
              }
              readOnly={form.is_reversal}
              required
              step="0.01"
              type="number"
              value={form.payment_amount}
            />
          </label>
          <label htmlFor="payment-date">
            Paid date
            <TypedDateInput
              data-testid="invoice-payment-date-input"
              disabled={form.is_reversal}
              id="payment-date"
              onChange={(value) => setForm({ ...form, payment_date: value })}
              value={form.payment_date}
            />
          </label>
          <label className="invoice-editor-wide" htmlFor="payment-reference">
            Payment reference
            <Input
              data-testid="invoice-payment-reference-input"
              disabled={form.is_reversal}
              id="payment-reference"
              onChange={(event) =>
                setForm({ ...form, payment_reference: event.target.value })
              }
              value={form.payment_reference}
            />
          </label>
          <label className="invoice-editor-wide" htmlFor="payment-notes">
            {form.is_reversal ? "Reversal reason" : "Payment notes"}
            <Textarea
              data-testid="invoice-payment-notes-input"
              id="payment-notes"
              onChange={(event) => setForm({ ...form, notes: event.target.value })}
              required={form.is_reversal}
              value={form.notes}
            />
          </label>
          <DialogFooter className="invoice-editor-wide">
            <Button
              data-testid="invoice-payment-save-button"
              disabled={saving}
              type="submit"
            >
              {saving
                ? "Saving…"
                : form.is_reversal
                  ? "Reverse payment"
                  : "Record payment"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
